import { CommonHelperFunctions, DashboardState, EUserAccessPart, IConfig } from "@kuika/kuika-cl-model";
import { MenuInfo } from "rc-menu/es/interface";
import * as React from "react";
import { connect } from "react-redux";
import LocalStorageService from "../../../auth/local-storage-service";
import { DomainService } from "../../../services/domain-service";
import { UserService } from "../../../services/user-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import { readThemePreference, ThemePreference } from "../../../shared/utility/designer-theme";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { changeThemePreference } from "../../../shared/utility/theme-transition";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import KuikaAvatar, { getIdentitySeed } from "../../ui-kit/kuika-avatar/kuika-avatar";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { MyProfileModal } from "../header/my-profile-modal";
import UserMenuAppearance, { nextThemePreference } from "./user-menu-appearance";

export interface IUserMenuButtonProps {
  activeGlobalConfiguration?: IConfig;
  setNextCallbackFunction: (cb?: () => void) => void;
}

interface IUserMenuButtonState {
  isDropdownVisible: boolean;
  isMyProfileModalActive: boolean;
  profilePhoto: string;
  isDomainAuthorizationEnabled: boolean;
  isAuthSwitchLoading: boolean;
  themePreference: ThemePreference;
}

declare let window: Window & { profilePhoto?: string };

/**
 * Bottom-left account avatar for the design (/sd) shell. Hosts the account menu that
 * used to live on the Kuika logo (the logo now navigates straight to Apps).
 */
class UserMenuButton extends React.Component<
  IUserMenuButtonProps & IWithSDProps & IWithContextProp & IWithHistoryProp,
  IUserMenuButtonState
> {
  state: IUserMenuButtonState = {
    isDropdownVisible: false,
    isMyProfileModalActive: false,
    profilePhoto: "",
    isDomainAuthorizationEnabled: KMainFunctions.isDomainAuthorizationEnabled(),
    isAuthSwitchLoading: false,
    themePreference: readThemePreference()
  };

  componentDidMount(): void {
    this.setState({
      profilePhoto: this.props.context.profilePhoto || this.props.context.userInfo?.profilePhotoUrl || ""
    });
  }

  // Leaving the current screen with pending in-panel edits routes through the unsaved-changes
  // guard, mirroring the logo's original behaviour.
  navigateGuarded = (action: () => void): void => {
    const { sd, activeGlobalConfiguration } = this.props;
    const hasPendingWork: boolean = Boolean(
      sd?.activeConfiguration ||
      sd?.activeEditingDatasource ||
      sd?.activeCreatingDatasourceType !== undefined ||
      sd?.creatingActionsDatasource ||
      sd?.editingTable ||
      sd?.editingAction ||
      sd?.activeCreatingTableName ||
      sd?.activeCreatingActionName ||
      sd?.editingNotificationTemplate ||
      sd?.getSelectedApi() ||
      activeGlobalConfiguration ||
      sd.dashboardState === DashboardState.runSettings
    );
    if (hasPendingWork) {
      this.props.setNextCallbackFunction(action);
      return;
    }
    action();
  };

  gotoApps = (): void => {
    const { sd, navigator } = this.props;
    this.navigateGuarded(() => {
      navigator.gotoProjects();
      sd.setCreateNotificationTemplateActive();
      sd.clearNotificationTemplatePanel();
      sd.cancelEditingScheduledJob();
      sd.cancelEditingApi();
      sd.cancelActiveMethod();
      sd.cancelCreatingOrEditingDatasource();
      sd.cancelCreatingOrEditingConfiguration();
      sd.clearSelectedWf();
    });
  };

  gotoGlobalConfig = (): void => {
    this.navigateGuarded(() => this.props.navigator.goToGlobalConfig());
  };

  saveScreen = async (): Promise<void> => {
    await this.props.sd.saveScreen();
  };

  logout = (): void => {
    UserService.Logout(this.props.sd?.sdId);
  };

  // Workspace-wide switch, so it writes through the domain service AND mirrors the result into
  // local storage — KMainFunctions.isDomainAuthorizationEnabled() reads from there, and the
  // users screen gates its owner/transfer actions on that same read.
  handleDomainAuthorizationChange = async (checked: boolean): Promise<void> => {
    this.setState({ isAuthSwitchLoading: true });
    try {
      const currentDomain: string = this.props.navigator.getCurrentDomain();
      const res = await DomainService.UpdateDomainAuthorization(currentDomain, checked);
      if (res && res.status === 200) {
        LocalStorageService.setDomainAuthInfo(checked.toString());
        this.setState({ isDomainAuthorizationEnabled: checked });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isAuthSwitchLoading: false });
    }
  };

  // The shell mounts this menu in more than one place; re-read the saved preference on every
  // open so a pick made in another one is what this one shows.
  handleDropdownVisibleChange = (isVisible: boolean): void => {
    if (isVisible) {
      this.setState({ isDropdownVisible: true, themePreference: readThemePreference() });
      return;
    }
    this.setState({ isDropdownVisible: false });
  };

  handleThemePreferenceChange = (preference: ThemePreference): void => {
    this.setState({ themePreference: preference });
    changeThemePreference(preference);
  };

  // A pointer press lands on one of the three options, which handle it themselves; the row is
  // not a button. Enter on the row (rc-menu reports it as a click) steps to the next option,
  // which is how the keyboard reaches the control — rc-dropdown closes the menu on Tab.
  handleAppearanceRowClick = (info: MenuInfo): void => {
    if (info.domEvent.type !== "keydown") {
      return;
    }
    this.handleThemePreferenceChange(nextThemePreference(this.state.themePreference));
  };

  getMenuOptions = (): KuikaDropdownMenuItemType[] => {
    const ctrlKey: string = CommonHelperFunctions.isMac() ? "Command" : "Ctrl";
    const hasAccessManageConfigurations: boolean = KMainFunctions.hasUserAccessInWorkspace(
      EUserAccessPart.ManageConfigurations
    );
    // The only account menu in BOTH shells now, and the two shells want different things
    // from it. Inside /sd it stays the full design menu. On the management side it is the
    // account menu only — Apps and Global Configuration are one click away in the left
    // menu strip there, and there is no screen to save.
    const isInSdPath: boolean = this.props.navigator.location.pathname.includes("/sd");
    const isDomainOwner: boolean = this.props.context?.userInfo?.isDomainOwner === true;

    const options: (KuikaDropdownMenuItemType | null)[] = [
      // System / Light / Dark, saved for this browser; dark is the default for everyone. The menu
      // stays open so the themes can be compared. First in the menu, in a group of its own (so a
      // divider sets it off from the rest): a small "Theme" caption over a control filling the row.
      // The test id the E2E suite clicks is pinned, since a ReactNode label cannot derive it.
      {
        key: "appearance",
        label: <UserMenuAppearance value={this.state.themePreference} onChange={this.handleThemePreferenceChange} />,
        dataTestId: "dropdownAppearanceButton",
        group: 0,
        keepOpenOnSelect: true,
        noHighlight: true,
        onClick: this.handleAppearanceRowClick
      },
      isInSdPath
        ? {
            key: "apps",
            label: "Apps",
            group: 1,
            icon: <KuikaSvg data-testid="userMenuAppsButton" name="apps" width={16} height={16} />,
            onClick: this.gotoApps
          }
        : null,
      isInSdPath && hasAccessManageConfigurations
        ? {
            key: "global-configuration",
            label: "Global Configuration",
            group: 1,
            icon: (
              <KuikaSvg
                data-testid="userMenuGlobalConfigButton"
                type="sidebar-icons"
                name="device-desktop-cog"
                width={16}
                height={16}
              />
            ),
            onClick: this.gotoGlobalConfig
          }
        : null,
      isInSdPath
        ? {
            key: "save",
            label: "Save",
            group: 2,
            icon: <KuikaSvg data-testid="userMenuSaveButton" name="save" width={16} height={16} />,
            rightSlot: `${ctrlKey} + S`,
            onClick: this.saveScreen
          }
        : null,
      {
        key: "view-profile",
        label: "View Profile",
        group: 3,
        icon: <KuikaSvg data-testid="userMenuViewProfileButton" name="user-circle" width={16} height={16} />,
        onClick: () => this.setState({ isMyProfileModalActive: true })
      },
      // Workspace authorization — back in the account menu, where it lived before it was
      // pulled out onto the management header row. Management-only (there is no workspace to
      // configure from inside /sd) and owner-only, exactly the gate it originally carried. The
      // label states the ACTION, so its wording is the state read-out: "Disable…" means it is on.
      !isInSdPath && isDomainOwner
        ? {
            key: "workspace-authorization",
            label: this.state.isDomainAuthorizationEnabled
              ? "Disable workspace authorization"
              : "Enable workspace authorization",
            group: 4,
            disabled: this.state.isAuthSwitchLoading,
            icon: (
              <KuikaSvg data-testid="userMenuWorkspaceAuthorizationButton" name="users-group" width={16} height={16} />
            ),
            onClick: () => this.handleDomainAuthorizationChange(!this.state.isDomainAuthorizationEnabled)
          }
        : null,
      {
        key: "sign-out",
        // `danger` is how the primitive says this, in `--red-5`. The label and the glyph
        // each carried their own `rgba(224, 68, 65, 1)`, which is neither that token nor
        // anything the dark theme can remap.
        label: <span data-testid="userMenuSignOutButton">Sign out</span>,
        group: 5,
        danger: true,
        icon: <KuikaSvg name="logout" width={16} height={16} />,
        onClick: this.logout
      }
    ];

    return options.filter((option): option is KuikaDropdownMenuItemType => option !== null);
  };

  render() {
    const { isMyProfileModalActive, isDropdownVisible, profilePhoto } = this.state;
    return (
      <div className="kuika-left-menu__user-menu">
        {isMyProfileModalActive && (
          <MyProfileModal
            showModal={isMyProfileModalActive}
            closeModal={() => this.setState({ isMyProfileModalActive: false })}
          />
        )}
        <KuikaDropdownMenu
          options={this.getMenuOptions()}
          open={isDropdownVisible}
          setOpen={this.handleDropdownVisibleChange}
          trigger="click"
          placement="bottomRight"
          offset={[0, -8]}
          overlayClassName="kuika-logo__dropdown kuika-user-menu__dropdown"
        >
          <KuikaAvatar
            data-testid="userMenuAvatar"
            data-onboarding-id="left_menu_user_avatar"
            size={32}
            className="kuika-user-menu__avatar"
            src={window.profilePhoto || profilePhoto}
            seed={getIdentitySeed(this.props.context.userInfo)}
            // A keyboard trigger on its own: Enter/Space dispatch a click, which bubbles to the
            // dropdown's trigger wrapper exactly as a pointer click does.
            interactive
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={isDropdownVisible}
          >
            {KMainFunctions.getUserLettersToShow(this.props.context.userInfo)}
          </KuikaAvatar>
        </KuikaDropdownMenu>
      </div>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  activeGlobalConfiguration: state.globalConfig.activeGlobalConfiguration
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const userMenuButton = connect(
  mapStateToProps,
  mapDispatchToProps
)(withHistory<IUserMenuButtonProps>(withContext<IUserMenuButtonProps>(withSD<IUserMenuButtonProps>(UserMenuButton))));
export { userMenuButton as UserMenuButton };
